<template>
    <widget-card>
        <template #header>
            <Grid class="widget-icon" />
            <span class="widget-card-header__title">
                {{ trans('dashboard.v2.testing_compliance.by_line.title') }}
            </span>
        </template>

        <div v-if="loading" class="flex flex-col gap-2">
            <SkeletonLoader v-for="i in 4" :key="i">
                <div class="h-14 w-full rounded-lg"></div>
            </SkeletonLoader>
        </div>

        <empty-widget-state v-else-if="!airports.length" :text="trans('dashboard.v2.testing_compliance.by_line.empty_state')" />

        <div v-else ref="shellRef" class="by-line-shell">
            <svg
                v-if="pairs.length"
                class="by-line__rail"
                :width="railWidth"
                :height="railHeight"
                :viewBox="`0 0 ${railWidth} ${railHeight}`"
                aria-hidden="true"
            >
                <g
                    v-for="(pair, idx) in pairs"
                    :key="idx"
                    stroke="currentColor"
                    stroke-width="1"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    fill="none"
                >
                    <path :d="bracePath(pair)" stroke-dasharray="1.75 2" />
                    <line :x1="ARROW_LEFT" :y1="pair.gapY" :x2="ARROW_TIP_X" :y2="pair.gapY" stroke-dasharray="1.75 2" />
                    <path :d="`M ${ARROW_TIP_X - 3} ${pair.gapY - 2.5} L ${ARROW_TIP_X} ${pair.gapY} L ${ARROW_TIP_X - 3} ${pair.gapY + 2.5}`" />
                </g>
            </svg>

            <div class="by-line">
                <template v-for="(group, groupIndex) in airports" :key="group.id">
                    <div
                        v-if="airports.length >= 2"
                        class="by-line__airport-header"
                        :class="{ 'by-line__airport-header--separator': groupIndex > 0 }"
                    >
                        {{ group.name }}
                    </div>
                    <div v-if="group.layout.grouped_header" class="by-line__head">
                        <div>{{ trans('dashboard.v2.testing_compliance.by_line.lines_header') }}</div>
                        <div>{{ trans('dashboard.v2.testing_compliance.by_line.assets_header') }}</div>
                        <div class="pl-3">{{ group.layout.shared.label }}</div>
                    </div>
                    <div v-else class="by-line__head by-line__head--columns">
                        <div class="by-line__head-main">
                            <!-- Intentionally blank: the line-name column has no header (row_header_label was removed); the cell stays to keep the grid aligned with the rows below. -->
                            <div></div>
                            <div class="grid gap-2" :style="{ gridTemplateColumns: `repeat(${group.layout.columns.length}, minmax(0, 1fr))` }">
                                <div v-for="(column, columnIndex) in group.layout.columns" :key="columnIndex">{{ column.label }}</div>
                            </div>
                        </div>
                        <div class="pl-3">{{ group.layout.shared.label }}</div>
                    </div>
                    <template v-for="area in group.areas" :key="area.id">
                        <div v-if="area.name" class="by-line__area-header">
                            {{ area.name }}
                        </div>
                        <div class="by-line__block">
                            <div class="flex flex-col gap-0.5" :class="{ 'by-line__lines--indented': area.name }">
                                <div v-for="line in area.lines" :key="line.id" :ref="(el) => setLineRef(line.id, el)" class="by-line__row">
                                    <div class="text-sm font-semibold text-body">{{ line.name }}</div>
                                    <div
                                        class="grid gap-2"
                                        :style="{ gridTemplateColumns: `repeat(${group.layout.columns.length}, minmax(0, 1fr))` }"
                                    >
                                        <div v-for="(column, columnIndex) in group.layout.columns" :key="columnIndex" class="min-h-[36px]">
                                            <TestingComplianceAssetBadge
                                                v-for="asset in assetsInColumn(line, column)"
                                                :key="asset.id"
                                                :label="asset.label"
                                                :curie-id="asset.curie_id"
                                                :status="cellStatus(asset, group)"
                                                :operability="asset.operability"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="by-line__block-section">
                                <TestingComplianceAssetBadge
                                    v-for="item in area.shared"
                                    :key="item.id"
                                    :label="item.label"
                                    :curie-id="item.curie_id"
                                    :status="cellStatus(item, group)"
                                    :operability="item.operability"
                                />
                            </div>
                        </div>
                    </template>
                </template>
            </div>
        </div>
    </widget-card>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick, computed } from 'vue';
import WidgetCard from '@/Pages/DashboardV2/Components/WidgetCard.vue';
import EmptyWidgetState from '@/Pages/DashboardV2/Components/EmptyWidgetState.vue';
import SkeletonLoader from '@/Components/SkeletonLoader.vue';
import TestingComplianceAssetBadge from '@/Pages/DashboardV2/Components/TestingCompliance/TestingComplianceAssetBadge.vue';
import Grid from '@/../icons/Grid.vue';
import { eventBus, events } from '@/eventBus';
import { $http } from '@/bootstrap';
import { trans } from '@/mixins/i18n';
import { buildUrlWithQuery } from '@/Utils/buildUrlWithQuery';

const props = defineProps({
    widgetData: {
        type: Object,
        required: true,
        default: () => ({}),
    },
    filters: {
        type: Object,
        default: () => ({}),
    },
});

const dataUrl = computed(() => props.widgetData.dataUrl);

const loading = ref(true);
const airports = ref([]);

// The page-header shift switcher is a single global control, so (matching the
// backend donuts) it only applies when exactly one shift airport is in scope.
// With several shift airports each falls back to its own current shift — a
// shared label can't drive airports on different clocks. Each asset carries
// every shift, so cells re-render reactively once the selection changes.
const selectedShift = ref(null);
const shiftAirportCount = computed(() => airports.value.filter((airport) => airport.layout?.shifts).length);

const shiftFor = (group) => {
    const shifts = group.layout.shifts;
    if (!shifts) {
        return null;
    }
    const ownCurrent = shifts.current ?? shifts.options?.[0]?.value ?? null;

    return shiftAirportCount.value === 1 ? (selectedShift.value ?? ownCurrent) : ownCurrent;
};

// The leading status icon tracks the resolved shift when the airport runs
// shifts; otherwise it falls back to the asset's day status.
const cellStatus = (asset, group) => {
    const shift = shiftFor(group);
    if (shift && asset.shifts) {
        return asset.shifts[shift] ?? 'unscheduled';
    }

    return asset.status;
};

const shellRef = ref(null);
const lineRefs = new Map();
const pairs = ref([]);
const railHeight = ref(0);

// Rail geometry. Keep tight — space on the left is precious.
const railWidth = 22;
const BRACKET_X = 1.5;
const CHEVRON_TIP_DX = 6;
const CHEVRON_HALF_H = 15.5;
const BRACKET_INSET = 5;
// Arrow starts right where the bracket's chevron tip lands so the arrow looks
// like a continuation of the chevron, not a separate marker.
const ARROW_LEFT = BRACKET_X + CHEVRON_TIP_DX;
const ARROW_TIP_X = 20;

// Vertical dashed line with a single chevron-bump pointing right, anchored
// to the gap between the two rows so it aligns horizontally with the side
// arrow regardless of row-height asymmetry. Proportions copied from
// Vector 633.png. Falls back to a plain vertical line when the chevron
// cannot fit between the rows.
const bracePath = (pair) => {
    const top = pair.top;
    const bottom = pair.bottom;
    const peakY = pair.gapY;
    const availableHalf = Math.min(peakY - top, bottom - peakY) - 1;

    if (availableHalf < 8) {
        return `M ${BRACKET_X} ${top} V ${bottom}`;
    }

    const halfH = Math.min(CHEVRON_HALF_H, availableHalf);
    const tipX = BRACKET_X + CHEVRON_TIP_DX;
    const chevronStartY = peakY - halfH;
    const chevronEndY = peakY + halfH;

    return (
        `M ${BRACKET_X} ${top}` +
        ` V ${chevronStartY}` +
        ` C ${BRACKET_X} ${chevronStartY + halfH * 0.247}` +
        ` ${BRACKET_X + CHEVRON_TIP_DX * 0.179} ${chevronStartY + halfH * 0.49}` +
        ` ${BRACKET_X + CHEVRON_TIP_DX * 0.515} ${chevronStartY + halfH * 0.699}` +
        ` L ${tipX} ${peakY}` +
        ` L ${BRACKET_X + CHEVRON_TIP_DX * 0.484} ${peakY + halfH * 0.334}` +
        ` C ${BRACKET_X + CHEVRON_TIP_DX * 0.167} ${peakY + halfH * 0.539}` +
        ` ${BRACKET_X} ${peakY + halfH * 0.773}` +
        ` ${BRACKET_X} ${chevronEndY}` +
        ` V ${bottom}`
    );
};

const setLineRef = (id, el) => {
    if (el) {
        lineRefs.set(id, el);
    } else {
        lineRefs.delete(id);
    }
};

const fetchData = async (showLoading = false) => {
    try {
        loading.value = showLoading;
        const formData = props.filters?.form?.data?.() ?? {};
        // The shift filter lives in the shared query params; the deep `filters`
        // watcher re-runs this on change, so the cells follow the selected shift.
        selectedShift.value = formData.shift ?? null;
        const url = buildUrlWithQuery(dataUrl.value, { locations: formData.locations });
        const response = await $http.get(url);
        airports.value = response.data.airports ?? [];
    } catch (error) {
        airports.value = [];
        console.error('Failed to fetch testing compliance by line:', error);
    } finally {
        loading.value = false;
    }
};

// A column groups asset-type strings that share a visual slot
// (e.g. ["WTMD", "FBS"] — a line has one OR the other, never both).
const assetsInColumn = (line, column) => line.assets.filter((asset) => column.types.includes(asset.type));

// Draw one flow bracket in every gap between consecutive physical area
// blocks (PSOB groups) — not between lines within a group. Each bracket
// spans the last row of the upper group to the first row of the lower group,
// with the chevron/arrow sitting in the physical gap between them.
// Named areas (Private Search Room, HHMD Area) and non-flow lines are
// skipped, so brackets stop after the last screening-lane group.
const computePairs = () => {
    if (!shellRef.value) {
        pairs.value = [];
        railHeight.value = 0;
        return;
    }

    const wrapperRect = shellRef.value.getBoundingClientRect();
    railHeight.value = wrapperRect.height;

    const result = [];
    for (const group of airports.value) {
        if (group.layout?.show_flow_brackets !== true) continue;

        // One entry per unnamed area (PSOB group) that has at least one flow
        // line. Named areas (Private Search Room, HHMD Area, etc.) are
        // excluded — brackets only connect the main screening-lane groups.
        const flowAreas = [];
        for (const area of group.areas) {
            if (area.name !== null) continue;
            const flowLines = area.lines.filter((l) => l.participates_in_flow !== false);
            if (flowLines.length === 0) continue;
            const firstEl = lineRefs.get(flowLines[0].id);
            const lastEl = lineRefs.get(flowLines[flowLines.length - 1].id);
            if (!firstEl || !lastEl) continue;
            flowAreas.push({
                firstRect: firstEl.getBoundingClientRect(),
                lastRect: lastEl.getBoundingClientRect(),
            });
        }

        // One bracket per gap between consecutive flow areas. Each bracket is
        // anchored to the last row of area[i] and the first row of area[i+1]
        // so adjacent brackets don't overlap even when a group has >1 line.
        for (let i = 0; i + 1 < flowAreas.length; i++) {
            const a = flowAreas[i];
            const b = flowAreas[i + 1];
            const gapCenter = (a.lastRect.bottom + b.firstRect.top) / 2;
            result.push({
                top: a.lastRect.top - wrapperRect.top + BRACKET_INSET,
                bottom: b.firstRect.bottom - wrapperRect.top - BRACKET_INSET,
                gapY: gapCenter - wrapperRect.top,
            });
        }
    }

    pairs.value = result;
};

let resizeObserver = null;

const startObservers = () => {
    if (!shellRef.value) {
        return;
    }
    resizeObserver?.disconnect();
    resizeObserver = new ResizeObserver(computePairs);
    resizeObserver.observe(shellRef.value);
    for (const el of lineRefs.values()) {
        resizeObserver.observe(el);
    }
};

watch([airports, loading], async () => {
    await nextTick();
    startObservers();
    computePairs();
});

watch(
    () => props.filters,
    () => fetchData(),
    { deep: true },
);

onMounted(() => {
    fetchData(true);
    eventBus.$on(events.dashboardWidgetRefresh, fetchData);
    window.addEventListener('resize', computePairs);
});

onBeforeUnmount(() => {
    eventBus.$off(events.dashboardWidgetRefresh, fetchData);
    resizeObserver?.disconnect();
    window.removeEventListener('resize', computePairs);
});
</script>

<style scoped>
.by-line-shell {
    position: relative;
    padding-left: 24px;
}

.by-line__rail {
    position: absolute;
    left: 0;
    top: 0;
    color: var(--color-primary);
    opacity: 0.5;
    pointer-events: none;
}

.by-line {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    overflow-x: auto;
}

.by-line__head,
.by-line__block,
.by-line__airport-header,
.by-line__area-header {
    min-width: 900px;
}

.by-line__airport-header {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--color-body);
    padding: 0 0.75rem;
    /* Pull the column header right under the Airport name. The `.by-line`
       `gap` would otherwise leave too much vertical space between the
       Airport label and the columns it heads. */
    margin-bottom: -0.5rem;
}

.by-line__airport-header--separator {
    border-top: 1px solid var(--color-panel-border);
    padding-top: 1rem;
    margin-top: 0.5rem;
}

/* Same treatment as the airport header: bold name pulled tight onto the
   block it heads, with a muted line count trailing it. */
.by-line__area-header {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--color-body);
    padding: 0 0.75rem;
    margin-bottom: -0.5rem;
}

/* Nest the line rows visually under their area header. */
.by-line__lines--indented {
    padding-left: 1.25rem;
}

.by-line__head {
    display: grid;
    grid-template-columns: 80px 1fr 200px;
    gap: 1rem;
    padding: 0 0.75rem;
    font-size: 0.75rem;
    color: var(--color-gray-text);
    font-weight: 500;
}

.by-line__head--columns {
    grid-template-columns: minmax(0, 1fr) 200px;
}

.by-line__head-main {
    display: grid;
    grid-template-columns: 80px 1fr;
    gap: 1rem;
}

.by-line__block {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 200px;
    gap: 1rem;
    align-items: center;
}

.by-line__block-section {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    background: var(--color-panel);
    border-radius: 0.5rem;
    min-height: 36px;
}

.by-line__block-section:empty {
    background: transparent;
}

.by-line__row {
    display: grid;
    grid-template-columns: 80px 1fr;
    align-items: center;
    gap: 1rem;
    background: var(--color-panel);
    border-radius: 0.5rem;
    padding: 0.75rem;
}
</style>
